import {z} from 'zod';

import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import askAboutObjectsTool from '../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import {CLStore} from 'common/src/redux/store';
import {CampaignSetupAgentToolName} from '../../campaignSetupAgent.enums';
import verifyCampaignPathTool from '../../tools/general/verifyCampaignPathTool/verifyCampaignPathTool';
import {CampaignStep} from 'common/src/modules/campaignWorkflow/campaignWorkflow.enums';
import addPlacementsTool from './tools/addPlacementsTool';
import editPlacementsTool from './tools/editPlacementsTool';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import deletePlacementsTool from './tools/deletePlacementsTool';
import getPlacementsSummary from './utils/getPlacementsSummary';
import formatPlacementsSummary from './utils/formatPlacementsSummary';

const placementsManagementToolSchema = z.object({
  prompt: z.string().describe('Prompt to manage placements'),
});

export const responseSchema = z.object({
  success: z
    .boolean()
    .describe('The status of the agent, in case of failure, return false, otherwise return true'),
  chainOfThoughts: z
    .string()
    .describe('Summary of the actions taken and tools used, that lead to the status'),
});

export default function placementsManagementTool(store: CLStore) {
  async function execute(params: ToolExecuteParams<typeof placementsManagementToolSchema>) {
    const {input, toolUtils, callChain} = params;

    const {prompt} = input;

    const placementsSummary = getPlacementsSummary(store);
    const placementsSummaryText = formatPlacementsSummary(placementsSummary);

    const instructions = `
You are a specialist in managing campaign placements.
- A placement is identified by plcId (textual) and dspId (numeric id of the DSP from DSPs library). For every action on specific placement, this info should be provided. You can find the DSP by it's name using ${CampaignSetupAgentToolName.AskAboutObjects} tool.
- A placement defines a specific audience segment.
- Placement fields: plcId, dspId, adSize (all or specific), environment, custom parameters, external identifiers.
Existing placements:
${placementsSummaryText}

## Instructions
You should follow the prompt and add/edit/delete placements accordingly.
- Placement is identified by its placement id (plcId) and dsp id (dspId).
- All your internal tools will return results based on this key (plcId-dspId).
- To list existing placements in the current campaign version, use the ${CampaignSetupAgentToolName.AskAboutObjects} tool.`;

    const placementsManager = standaloneAgent({
      name: 'placementsManager',
      role: 'Manage campaign placements',
      instructions,
      tools: [
        askAboutObjectsTool(store),
        verifyCampaignPathTool(CampaignStep.Placements, store),
        addPlacementsTool(store),
        editPlacementsTool(store),
        deletePlacementsTool(store),
      ],
      responseSchema,
    });

    const result = await toolUtils.invokeStandaloneAgent({
      agent: placementsManager,
      prompt,
      callChain,
      scratchpad: {},
    });

    if (!result.success) {
      return toolError(JSON.stringify(result));
    }

    return toolResult(result.chainOfThoughts);
  }

  return tool({
    name: 'placementsManagement',
    displayName: 'Working on placements',
    description: `Allow to Add/Update/Delete campaign placements.`,
    inputSchema: placementsManagementToolSchema,
    execute,
  });
}
